import React from "react";
import moment from "moment";
import * as H from "../../../../hooks";
import * as C from "../../../../Common";
import { Alerts } from "../../MiscModal";
import * as S from "../../../../services";
import { renderLoader } from "../../Loader";
import { TC, DS, TB } from "../../../../Constants";
import { DataSet } from "../../../../Constants/Types/Data";
import { StyleModalProps, default as Modal } from "../../BlankModal";
//#region Types
export type ReportEnergyGraphsProps = {
    /** Id of the building */
    building: string;
    /** The entities to focus on */
    entities: (Pick<DataSet, "_id" | "name" | "type" | "src"> & Record<"parent" | "path_parent", string>)[];
    /** The end time limit */
    to: string;
    /** The start time limit */
    from: string;
    /** The type of group to do */
    group: string;
    /** Extra Modal params */
    modal?: StyleModalProps;
    /** Callback for cancelation */
    onQuit?: () => void;
}

type Entity = {
    /** The id of the entity */
    id: string;
    /** The type of graph to display */
    type: string[];
    /** Does it include datasets under formulas ? */
    insideItems: boolean;
}
//#endregion

const ReportEnergyGraphs: React.FC<ReportEnergyGraphsProps> = props => {
    const lg = H.useLanguage();
    const [entities, set_entities] = React.useState<Entity[]>([]);
    const [all_entities, set_all_entities] = React.useState<Entity>({ id: "all", type: ['bar', 'heatmap'], insideItems: false });
    const [default_type, set_default_type] = React.useState<string[]>(['bar', 'heatmap']);

    const graphTypes = React.useMemo(() => {
        const isMoreThan3Months = props.from && props.to && moment(props.to).diff(moment(props.from), 'months', true) > 2;

        const types = [
            { label: lg.getStaticText(TC.NRJ_DASH_SUB_MENU_BAR), value: "bar" },
            { label: lg.getStaticText(TC.NRJ_DASH_SUB_MENU_INDEX), value: "line" },
            { label: lg.getStaticText(TC.NRJ_DASH_SUB_PANEL_TABLE), value: "table" }
        ];

        if (!isMoreThan3Months) {
            types.splice(2, 0, {
                label: lg.getStaticText(TC.NRJ_DASH_SUB_MENU_HEATMAP),
                value: "heatmap"
            });
        }

        return types;
    }, [lg, props.from, props.to]);

    React.useEffect(() => {
        const entities = props.entities.map(e => ({ id: e._id, type: default_type, insideItems: false }));
        set_entities(entities);
    }, [props.entities, default_type]);

    React.useEffect(() => {
        // If the time range is more than 2 months, only bar is available
        if (props.from && props.to && moment(props.to).diff(moment(props.from), 'months', true) > 2) {
            set_default_type(['bar']);
            set_all_entities({ id: "all", type: ['bar'], insideItems: false });
        }
    }, [props.from, props.to]);

    //#region Datasets HTML
    const datasetsHTML = React.useMemo(() => (
        <C.Flex direction="column" justifyContent="between">
            {/* Ligne des titres */}
            <C.Flex direction="row" alignItems="center" className="mb-2 fw-bold">
                <div className="w-75">{lg.getStaticText(TC.NRJ_DASH_DATASETS)}</div>
                <div className="w-75">{lg.getStaticText(TC.CHART_CONFIG_MODAL_CHART_TYPE)}</div>
                <div className="w-25 fs-85 ml-2 text-center">{lg.getStaticText(TC.NRJ_DATASET_UNDER_FORMULAS)}</div>
            </C.Flex>

            {/* Ligne "tous les datasets" */}
            <C.Flex direction="row" alignItems="center" key="all" className="mb-2">
                <div className="w-75">{lg.getStaticText(TC.ENERGY_ALL_DATASETS)}</div>
                <C.Form.Select
                    multiple
                    noBottomMargin
                    customClass="w-75"
                    options={graphTypes.map(t => ({ label: t.label, value: t.value }))}
                    value={all_entities?.type || []}
                    onChange={selectedTypes => {
                        set_all_entities({ id: "all", type: selectedTypes, insideItems: all_entities.insideItems });

                        set_entities(prev => {
                            const previous = all_entities.type;
                            const added = selectedTypes.filter(t => !previous.includes(t));
                            const removed = previous.filter(t => !selectedTypes.includes(t));

                            return prev.map(e => {
                                let updatedTypes = [...e.type, ...added.filter(t => !e.type.includes(t))];
                                updatedTypes = updatedTypes.filter(t => !removed.includes(t));
                                return { ...e, type: updatedTypes };
                            });
                        });
                    }}
                />
                <C.Form.CheckBox
                    value={all_entities.insideItems}
                    noBottomMargin
                    hideLabel
                    customClass="ml-2 w-25 text-center"
                    onChange={insideItems => {
                        set_all_entities(prev => ({ ...prev, insideItems }));

                        set_entities(prev =>
                            prev.map(e => ({
                                ...e,
                                insideItems,
                            }))
                        );
                    }}
                />

            </C.Flex>

            {/* Lignes individuelles */}
            {props.entities.map(p => (
                <C.Flex direction="row" alignItems="center" key={p._id} className="mb-2">
                    <div className="w-75">
                        <i className={`fa fa-${DS.ICONS[p.src]} me-2`}></i>{p.name} ({p.parent})
                    </div>
                    <C.Form.Select
                        multiple
                        noBottomMargin
                        customClass="w-75"
                        options={graphTypes.map(t => ({ label: t.label, value: t.value }))}
                        value={entities.find(e => e.id === p._id)?.type}
                        onChange={selectedTypes => set_entities(entities =>
                            entities.map(e => e.id === p._id ? { ...e, type: selectedTypes } : e)
                        )}
                    />
                    <C.Form.CheckBox
                        value={entities.find(e => e.id === p._id)?.insideItems}
                        noBottomMargin
                        hideLabel
                        customClass="ml-2 w-25 text-center"
                        onChange={insideItems => set_entities(entities =>
                            entities.map(e => e.id === p._id ? { ...e, insideItems } : e)
                        )}
                    />
                </C.Flex>
            ))}
        </C.Flex>
    ), [props.entities, graphTypes, entities, all_entities, lg]);

    //#endregion

    //#region Save
    const save = React.useCallback(() => {
        const data = {
            to: props.to,
            from: props.from,
            entity: entities,
            group: props.group,
        }
        const unmount = renderLoader(lg.getStaticText(TC.GLOBAL_GENERATING_FILE));

        S.file_report({ template: 'energy_graphs', params: data, filetype: "word" }).then(res => {
            // res.headers.file_loc
            if (res.data instanceof ArrayBuffer) TB.asyncArrayBufferToBase64(res.data).then(base64 => {

                S.getContextFullLoc({ roots: props.building }).then(context => {
                    const name_info = context.data.find(c => c.root === props.building);
                    const name = `${lg.getStaticText(TC.ENERGY_REPORT)} - ${name_info?.name}${name_info?.full_loc ? ' (' + name_info?.full_loc + ')' : ''}`;
                    TB.downloadFile(base64, name, 'docx');
                })
                    .catch(() => Alerts.loadError({ message: TC.GLOBAL_EXPORT_ERROR_OCCURRED }))
                    .finally(unmount);
            })
                .catch(() => {
                    unmount();
                    Alerts.loadError({ message: TC.GLOBAL_EXPORT_ERROR_OCCURRED });
                })
            else {
                unmount();
                Alerts.file_generation_fail();
            }
        }).catch(error => {
            unmount();
            Alerts.loadError(error);
        });
    }, [entities, props.to, props.from, props.group, props.building, lg,]);

    const save_button = React.useMemo(() => <C.Flex justifyContent="end">
        <C.Button onClick={save} icon="save" text={TC.GLOBAL_CONFIRM} />
    </C.Flex>, [save]);
    //#endregion

    return <Modal
        {...props.modal}
        footer={save_button}
        onQuit={props.onQuit}
        size={props.modal?.size || "xl"}
        title={props.modal?.title || TC.EXPORT_WORD}
    >
        {datasetsHTML}

    </Modal>;
}

export default ReportEnergyGraphs;